import produce from "immer";
import defaults from "lodash/defaults";
import isDate from "lodash/isDate";
import omit from "lodash/omit";
import type { AnyAction } from "redux";
import type { Epic } from "redux-observable";

import { isEqualTime } from "~/tools/date-time";
import { Logger } from "~/tools/logger";
import { EMPTY, distinctUntilChanged, map, mergeMap, of, pairwise } from "~/tools/rxjs";

import { warningMessageHalfYearLimit } from "../../../../components/react/timeline/TimelineUtils";
import { combineEpicsAndCatchErrors } from "../../../../generic/redux/CombineEpicsAndCatchErrors";
import { typedStream } from "../../../../generic/redux/TypedRedux";
import { validateSync } from "../../../../generic/Util";
import {
	type TimelineQuery,
	VALIDATION_ERROR_MORE_THAN_HALF_YEAR,
	adjustTimeRangeAndTimestamp,
	timelineSchema,
} from "../../../../routes/timeline/TimelineQueryUtils";
import { selectDocLinkUrlPrefix } from "../../config/ConfigSelectors";
import type { RootState } from "../../RootState";
import { mergeViewParams } from "../../route/RouteActions";

import {
	type GoLiveRequested,
	type TimelineUpdateRequested,
	goLiveSuccess,
	timestampUpdateSuccess,
} from "./TimelineActions";
import { GO_LIVE_REQUESTED, TIMELINE_UPDATE_REQUESTED } from "./TimelineActionTypes";
import { TimestampHistoryEpic } from "./TimelineHistoryEpic";
import {
	getLastSelectedDynamicInterval,
	selectDefaultTimeRange,
	selectTimelineQueryParams,
	selectTimestampDate,
} from "./TimelineSelectors";
import { adjustTimeRangeToHalfYearLimit } from "./TimeRange";

export const logger = Logger.namespace("TimelineEpic");

export const UpdateTimelineEpic: Epic<AnyAction, AnyAction, RootState> = (actions, states) =>
	actions.pipe(
		typedStream<TimelineUpdateRequested>(TIMELINE_UPDATE_REQUESTED),
		mergeMap((action) => {
			const defaultTimeRange = selectDefaultTimeRange(states.value);
			const timelineQueryParams = produce((timelineQueryParams) => {
				defaults(timelineQueryParams, { timeRange: defaultTimeRange });
			})(selectTimelineQueryParams(states.value));
			const query = adjustTimeRangeAndTimestamp(defaults(omit(action, "type"), timelineQueryParams) as TimelineQuery);
			const error = validateSync(timelineSchema, query);
			if (error != null) {
				if (error.type === VALIDATION_ERROR_MORE_THAN_HALF_YEAR) {
					const docLinkUrlPrefix = selectDocLinkUrlPrefix(states.value);
					warningMessageHalfYearLimit(docLinkUrlPrefix);
					const fixedTimeQuery = {
						...query,
						timeRange: adjustTimeRangeToHalfYearLimit(query.timeRange),
					};
					return of(mergeViewParams(fixedTimeQuery));
				}
				return EMPTY;
			}
			return of(mergeViewParams(query));
		}),
	);

export function equalDates(valueA: unknown, valueB: unknown): boolean {
	if (isDate(valueA) && isDate(valueB)) {
		return isEqualTime(valueA, valueB);
	}
	return valueA === valueB;
}

export const TimestampUpdateSuccessEpic: Epic<AnyAction, AnyAction, RootState> = (actions, states) =>
	states.pipe(map(selectTimestampDate), distinctUntilChanged(equalDates), map(timestampUpdateSuccess));

export const GoLiveSuccessEpic: Epic<AnyAction, AnyAction, RootState> = (actions, states) =>
	states.pipe(
		map(selectTimestampDate),
		distinctUntilChanged(equalDates),
		pairwise(),
		mergeMap(([prevTimestamp, timestamp]) => {
			if (prevTimestamp != null && timestamp == null) {
				return of(goLiveSuccess());
			}
			return EMPTY;
		}),
	);

export const GoLiveEpic: Epic<AnyAction, AnyAction, RootState> = (actions, states) =>
	actions.pipe(
		typedStream<GoLiveRequested>(GO_LIVE_REQUESTED),
		map(({ timeRange }) => {
			return mergeViewParams({
				timestamp: undefined,
				// when you go live (in the past you always have specific time range)
				// we should switch to last selected dynamic time range, e.g. LAST_10_MINUTES.
				timeRange: timeRange ?? getLastSelectedDynamicInterval(states.value),
			});
		}),
	);

export const TimelineEpic = combineEpicsAndCatchErrors(
	UpdateTimelineEpic,
	GoLiveEpic,
	TimestampUpdateSuccessEpic,
	GoLiveSuccessEpic,
	TimestampHistoryEpic,
);
